/**
 * Module for consuming, cacheing, and pooling datasource-manager end-points. Provides a Defer/Promise interface for handling repsonse and error events.
 * @name data-fetcher
 */
(function (factory) {
    if (typeof define === 'function' && define.amd) {
        // AMD. Register as an anonymous module.
        define([
            'mem-storage/mem-storage',
            'datasource-manager/datasource-manager',
            'app-console/app-console',
            'datasource-manager/lib/extend'
        ], factory);
    } else if (typeof module === 'object' && module.exports) {
        // CommonJS
        module.exports = factory(
            require('mem-storage/mem-storage'),
            require('datasource-manager/datasource-manager'),
            require('app-console/app-console'),
            require('datasource-manager/lib/extend')
        );
    }
})(function (memStorage, datasourceManager, appConsole, extend) {
    'use strict'
    var UNDEF = 'undefined';
    var root = (typeof window !== UNDEF) ? window : typeof global !== UNDEF ? global : this;
    var logger = new appConsole({ns: 'data-fetcher'});

    var CACHE_VALID = 'cache-valid';
    var CACHE_ERROR = 'cache-error';
    var SERVER = 'server';
    var SEMI = ';';
    var SPACE = ' ';
    var EMPTY = '';
    var RESOLVE = 'resolve';
    var REJECT = 'reject';

    // fetch consts
    var F_METHOD = 'GET';
    var F_MODE = 'cors';
    var F_CREDENTIALS = 'omit';
    var F_CACHE = 'default';
    var F_REDIRECT = 'follow';
    var F_POLICY = 'origin-when-cross-origin';

    var reqs = {};

    var SUCCESS = 'SUCCESS';
    var ERRORS = {
        CANCEL: 'REQUEST CANCELED',
        TIMEOUT: 'REQUEST TIMEDOUT',
        TRANSFORM: 'RESPONSE TRANSFORM',
        FAIL: 'REQUEST FAILURE'
    };

    var _resolve;
    var _reject;

    function promiseExecutor (resolve, reject) {
        _resolve = resolve;
        _reject = reject;
    }

    function FetchRequest (storageManager, req, requestKey, requestUrl, datasource, params) {
        var that = this;
        var fetchConfig = {
            method: F_METHOD,
            mode: F_MODE,
            credentials: F_CREDENTIALS,
            cache: F_CACHE,
            redirect: F_REDIRECT,
            referrerPolicy: F_POLICY
        };

        this.storageManager = storageManager;
        this.req = req;
        this.timeout = datasource.timeout * 1000;
        this.expires = datasource.expires;
        this.store = datasource.store !== false;
        this.datasource = datasource;
        this.datasourceName = datasource.name;
        this.params = params;
        this.namespace = datasource.getNamespace(params);
        this.requestKey = requestKey;
        this.fetchConfig = fetchConfig;
        this.fetchType = datasource.fetchType;

        if (datasource.method) {
            fetchConfig.method = datasource.method;
        }
        this.method = fetchConfig.method;
        
        if (datasource.body) {
            this.body = fetchConfig.body = datasource.getBody(params) || null;
        }

        if (datasource.fetch) {
            extend(fetchConfig, datasource.fetch);
        }

        if (datasource.headers || fetchConfig.headers) {
            fetchConfig.headers = new Headers(
                extend(datasource.getHeaders(params), fetchConfig.headers)
            );
        }

        this.url = requestUrl || null;
        this.fetchCatchCb = this.fetchCatch.bind(this);
        this.fetchDataCb = this.fetchData.bind(this);
        this.fetchCompleteCb = this.fetchComplete.bind(this);
        this.timeoutHandlerCb = this.timeoutHandler.bind(this);
        this.response = reqs[requestKey] = new Promise(promiseExecutor);
        this.resolve = _resolve;
        this.reject = _reject;
    }

    FetchRequest.prototype = {
        fetch: function (url, fetchConfig) {
            return fetch(url, fetchConfig);
        },
        response: null,
        retryRequested: false,
        complete: false,
        error: null,
        fetchResponse: { ok: false },
        requestRetry: function () {
            this.retryRequested = true;
        },
        fetchCatch: function (err) {
            this.error = err;
            this.clearTimeout();
            return this.returned(null);
        },
        fetchComplete: function (data) {
            this.clearTimeout();
            this.success = true;
            return this.returned(this.fetchResponse.ok ? data : null);
        },
        fetchData: function (fetchResponse) {
            var fetchType = this.fetchType;
            this.fetchResponse = fetchResponse;
            this.dataPromise.then(this.fetchCompleteCb, this.fetchCompleteCb)
                .catch(this.fetchCatchCb);
            return fetchResponse[fetchType] && fetchResponse[fetchType]();
        },
        timeoutHandler: function (e) {
            var error_code = ERRORS.TIMEOUT;
            logger.warn(error_code + SPACE + this.timeout, this.datasource.name, this.url);
            this.error = new Error(error_code);
            this.returned(null);
        },
        send: function () {
            var _url = this.url;
            if (_url !== null) {
                this.dataPromise = this.fetch(_url, this.fetchConfig)
                    .then(
                        this.fetchDataCb
                    ).catch(this.fetchCatchCb);
                this.setTimeout();
            } else {
                var error_code = ERRORS.CANCEL;
                logger.error(error_code, 'resolvedurl is null', this);
                this.error = new Error(error_code);
                this.returned(null);
            }
            return this.response;
        },
        clearTimeout: function () {
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
            }
        },
        setTimeout: function () {
            this.clearTimeout();
            this.timeoutId = setTimeout(this.timeoutHandlerCb, this.timeout);
        },
        tryTransform: function (fetchDataResponse, res) {
            var fetchType = this.fetchType;
            var responseData = {};
            responseData[fetchType] = fetchDataResponse;
            this.fetchResponse = fetchDataResponse;

            var data = this.datasource.transform(responseData, res, fetchDataResponse);

            if (!data) {
                data = null;
                res.error = new Error();
            }
            res.data = data;
            return res;
        },

        returned: function (fetchResponse) {
            if (this.complete) {
                return;
            }
            var data = null;
            var cached;
            var datasource = this.datasource;
            var res = this.req;
            var requestKey = this.requestKey;
            var storageKey = getMemStorageKey(datasource, this.params);
            var namespace = this.namespace;

            res.data = data;
            res.expired = false;
            res.source = SERVER;
            res.error = this.error || null;
            res.success = true;

            if (fetchResponse && !this.error) {
                // transform and hydrate object
                this.tryTransform(fetchResponse, res);
                data = res.data;
                if (data === null || res.error) {
                    res.success = false;
                    var error_code = ERRORS.TRANSFORM;
                    res.error.message = error_code + ' parse error from transformer :: ' + datasource.name + ' :: ' + res.error.message;
                    logger.error(error_code, res.error, data, this.rawUrl);
                }
            } else {
                logger.error(ERRORS.FAIL, res, data, this.url);
            }

            if (data !== null) {
                res.success = true;
                if (this.store !== false) {
                    this.storageManager.put(namespace, storageKey, data, datasource.expires);
                }
                // send success
                logger.log(SUCCESS, data, this.url, datasource);
            } else {
                cached = getNow(datasourceManager, this.storageManager, res, namespace, storageKey);
                data = res.data = cached.value;
                res.source = CACHE_ERROR;
                res.expired = !!cached.expired;
                res.expires = cached.expires;
                res.success = data !== null;
            }
            delete reqs[requestKey];
            this.complete = true;
            this[ data !== null ? RESOLVE : REJECT](res);
        }
    };

    /**
     * Convenience wrapper for retrieving a datasource from
     * DataSourceManager by name.
     *
     * @private
     *
     * @param  {string} dataSourceName Name of the datasource to retrive.
     * @return {object} DataSourceManager.DataSource instance.
     */
    function getDatasource (_datasourceManager, dataSourceName) {
        return _datasourceManager.get(dataSourceName);
    }
    /**
     * Synchonous retrieval of a datasource from the storage cache.
     * If no data is present, an empty StorageEntry will be returned.
     * @note exposes internal method getNow.
     *
     * @private
     *
     * @see  memStorage.get
     * @see  getNow
     * @param  {object} req Datasource descriptor object.
     * @return {mixed}      Storage.StorageEntry instance.
     */
    function getNow (datasourceManager, storageManager, req, namespace, storageKey) {
        var params = req.params;
        var dataSourceName = req.dataSourceName;
        var datasource = getDatasource(datasourceManager, dataSourceName);

        return storageManager.get(
            namespace || datasource.getNamespace(params),
            storageKey || getMemStorageKey(datasource, params)
        );
    }
    /**
     * Generate a unique key to store active requests
     * during the request/response lifecycle.
     *
     * @private
     *
     * @param  {object} datasource Datasource object.
     * @param  {object} params     Map of request variables.
     * @param  {boolean} [refresh] True/Falsey indicator if request should be
     *                             refreshed (no cached data).
     * @return {string}            Generated request key;
     */
    function getRequestKey (dataSourceName, _url) {
        return dataSourceName + SEMI + _url;
    }

    /**
     * Generate a storage key for use with the data cache.
     * Should be unique to the datasource, it's params.

     * @private
     *
     * @param  {object} datasource Instance of DataSource retrieved from
     *                             DataSourceManager
     * @param  {object} params     Map of request variables.
     *
     * @return {string}            Generated storage key.
     */
    function getMemStorageKey (datasource, params) {
        var storageKey = getRequestKey(datasource.name, datasource.getUrl(params));

        if (datasource.headers) {
            storageKey = storageKey + SEMI + JSON.stringify(datasource.getHeaders(params));
        }
        return storageKey;
    };

    /**
     * Module for consuming, caching, and pooling datasource-manager end-points.
     * Provides a Promise interface for handling repsonse and error events.
     * Provides in-memory caching via memStorage, or optional storageManager
     * override that implements the abstractStorage interface.
     * @param {[object]} options Optional configuration object for customizing
     *                           DataFetcher instances.
     */
    function DataFetcher (options) {
        options = options || {};
        this.datasourceManager = options.datasourceManager || datasourceManager.create();
        this.storageManager = options.storageManager || memStorage;

        if (options.FetchRequest) {
            this.FetchRequest = options.FetchRequest;
        }
    }

    /* Public API */
    DataFetcher.prototype = {
        /**
         * Clear the entire namespace related to a set of
         * cached datasource responses.
         * @param  {string} namespace Namespace to be cleared
         * @see  memStorage.clear
         * @return {object}     Returns DataFetcher for chaining.
         */
        clearNamespace: function (namespace) {
            this.storageManager.clear(namespace);
            return this;
        },
        /**
         * Remove a specific datasource response cache.
         * Once removed, response data is no longer accessible by
         * any means.
         *
         * @note To retain data, while still flagging for an update
         *       use DataFetcher.expire
         * @see memStorage.clear.
         * @param  {object} req Descriptor object indicating the datasource
         *                      and required params. @see DataFetcher.get for format.
         * @return {object}     Returns DataFetcher for chaining.
         */
        clearDataSource: function (req) {
            var datasource = getDatasource(this.datasourceManager, req.dataSourceName),
                params = req.params;

            this.storageManager.delete(
                datasource.getNamespace(params),
                getMemStorageKey(datasource, params)
            );
            return this;
        },
        /**
         * Clean the expired entries for namespace related to a set of
         * cached datasource responses.
         * @param  {string} namespace Namespace to be cleaned
         * @see  memStorage.clean
         * @return {object}     Returns DataFetcher for chaining.
         */
        cleanNamespace: function (namespace) {
            this.storageManager.clean(namespace);
            return this;
        },

        /**
         * Set a datasource reponse to expired. This will trigger
         * a server call on the next request.
         * @note    The data will still be accessible via .getNow,
         *          or returned in the event that an update fails.
         * @param  {[type]} req [description]
         * @return {object}     Returns DataFetcher for chaining.
         */
        expire: function (req) {
            var datasource = getDatasource(this.datasourceManager, req.dataSourceName),
                params = req.params;

            this.storageManager.expire(
                datasource.getNamespace(params),
                getMemStorageKey(datasource, params)
            );
            return this;
        },
        /**
         * Take a peek at the current active/pooled requests.
         * @return {object}     Returns DataFetcher for chaining.
         */
        peek: function () {
            return {
                reqs: reqs
            };
        },
        /**
         * Synchonous retrieval of a datasource from the storage cache.
         * If no data is present, an empty StorageEntry will be returned.
         * @note exposes internal method getNow.
         *
         * @see  memStorage.get
         * @see  getNow
         * @param  {object} req Datasource descriptor object.
         * @return {object}     Response descriptor object.
         */
        getNow: function (req) {
            var obj = getNow(this.datasourceManager, this.storageManager, req);

            req.data = obj.value;
            req.source = obj.error ? CACHE_ERROR : CACHE_VALID;
            req.expired = !!obj.expired;
            req.expires = obj.expires;
            req.refresh = false;
            req.err = null;

            return req;
        },
        /**
         * Asynchronous call to a datasource endpoint as defined by
         * DataSourceManager and matched by the descriptor object req.
         * Outgoing connections are sent into a request pool to avoid redundant
         * calls and cache entries.
         *
         * @param  {object} req Datasource descriptor object indicating a datasouce
         *                      and request options.
         * @example :
         *          DataFetcher.get({
         *                  // Named datasource from DataSourceManager
         *              dataSourceName  : {string},
         *                  // Map of variables and replacement values
         *                  // to be used in constructing the request url.
         *              params          : {object},
         *                  // Optional boolean to indicate a forced
         *                  // refresh of data.  If:
         *                  //      true  -  this disregards the expiration state of cached data.
         *                  //      false/undefined - follows expiration policy.
         *              refresh         : {boolean},
         *          });
         *
         * @uses   Promise
         * @uses   Fetch
         * @return {object}     Promise object for attaching callback handlers.
         */
        get: function (req) {
            var refresh = req.refresh = req.refresh === true;
            var params = req.params;
            var dataSourceName = req.dataSourceName;
            var datasourceManager = this.datasourceManager;
            var storageManager = this.storageManager;
            // Optional callback
            var callback = req.callback;
            var responsePromise;
            var cached;

            if (!refresh) {
                cached = getNow(datasourceManager, storageManager, req);
            }
            // If we have a valid cached copy of this request/response
            // wrap it in a promise, and return.
            if (cached && (!cached.error && cached.expired !== 1)) {
                // Valid cached data found.
                // Resolve the Promise immediately.
                req.refresh = refresh;
                req.data = cached.value;
                req.source = CACHE_VALID;
                req.expired = !!cached.expired;
                req.expires = cached.expires;
                req.error = null;
                responsePromise = Promise.resolve(req);
            } else {
                var currentRequest = req;
                var datasource = getDatasource(datasourceManager, dataSourceName);
                var requestUrl = datasource.getUrl(params);
                var requestKey = getRequestKey(dataSourceName, requestUrl);

                // Return a promise object request.
                // If a current request to this datasouce exists
                // piggy back off it to avoid wasted calls.
                responsePromise = reqs[requestKey] || (new this.FetchRequest(storageManager, req, requestKey, requestUrl, datasource, params)).send();
            }
            return responsePromise;
        },

        put: function (req, data) {
            req.refresh = req.refresh === true;
            var params = req.params;
            var dataSourceName = req.dataSourceName;
            var datasource = getDatasource(this.datasourceManager, dataSourceName);
            var dummyReq = new this.FetchRequest(this.storageManager, req, getRequestKey(dataSourceName, datasource.getUrl(params)), datasource, params);
            dummyReq.fetchResponse = {ok: true};
            dummyReq.fetchComplete(data);
            return this;
        },

        FetchRequest: FetchRequest,

        create: function (options) {
            return new DataFetcher(options);
        }
    };

    var api = new DataFetcher({
        storageManager: memStorage,
        datasourceManager: datasourceManager
    });

    api.DataFetcher = DataFetcher;

    return api;
});
